/* eslint-disable camelcase */
import { GridRowParams } from '@material-ui/data-grid';
import { ListItem, Typography } from '@material-ui/core';
import { observer } from 'mobx-react-lite';
import { formatToSloDate } from 'pages/Sessions/SessionsPage';
import { drawerStore } from 'state/drawerStore';
import ActionsMenu from 'components/other/ActionsMenu';
import { FormInputTypes } from 'utils/constants';
import { Api } from 'api';
import { gridColumn } from 'utils/gridUtils';
import TextLink from 'components/other/TextLink';
import {
  LOCATION_DETAILS_TABS,
  locationDetailsTabParameters,
  userDetailsTabParameters,
} from 'utils/routing';
import { PaymentStatusBadge } from 'components/other/CellBadges';
import { onClickShowCart } from './InvoicesPage';
import { usePaginatedProvider } from 'state/paginatedProviderStore';
import { VMPaginatedGrid } from 'components/main/VMPaginatedGrid';
import { PAYMENT_STATUS } from 'types/Payment';
import { TOAST_MESSAGE_TYPE, toastStore } from 'state/toastStore';

const onClickShowPaymentDetails = (payment) => {
  const date = new Date(payment?.createdAt);
  let sloDate: any;
  if (date) {
    sloDate = formatToSloDate(date);
  }

  const parsedValues = {
    ...payment,
    price: payment?.chargeAmount?.formatted,
    date: sloDate,
    ...payment?.purchaseMethod,
  };

  drawerStore.openDrawer({
    label: 'Payment details',
    inputs: [{ name: 'details', type: FormInputTypes.jsonView, value: parsedValues }],
    clickCallback: undefined,
    buttonLabel: 'Close',
  });

  drawerStore.setValues(parsedValues);
};

const paymentActions = (params: GridRowParams, paymentsProvider) => {
  const payment = params.row;
  const { receipt } = params.row;
  const { billingToken } = params.row;
  const cartId = params.row?.cartId;

  const finalizeCart = async () => {
    try {
      await Api.billing.cartForceFinalize(billingToken);
      paymentsProvider.reloadResource();

      toastStore.showMessage(
        `Payment with reference ${params.row.reference} is finalized.`,
        TOAST_MESSAGE_TYPE.INFO,
      );
    } catch (error) {
      toastStore.showMessage(
        `Failed to finalize payment with reference ${params.row.reference}.`,
        TOAST_MESSAGE_TYPE.ERROR,
      );
    }
  };

  return (
    <ActionsMenu>
      <ListItem button onClick={() => onClickShowPaymentDetails(payment)}>
        <Typography style={{ width: '100%' }}>Details</Typography>
      </ListItem>
      {receipt && (
        <ListItem button onClick={() => window.open(receipt, '_blank')}>
          <Typography style={{ width: '100%' }}>Receipt</Typography>
        </ListItem>
      )}
      {!receipt && params.row.status === PAYMENT_STATUS.CAPTURED && (
        <ListItem button onClick={finalizeCart}>
          <Typography style={{ width: '100%' }}>Finalize Cart</Typography>
        </ListItem>
      )}
      {params.row.status !== PAYMENT_STATUS.CAPTURED && (
        <ListItem
          button
          onClick={() => {
            Api.billing.cancelPayment(billingToken).then(() => {
              paymentsProvider.reloadResource();
            });
          }}
        >
          <Typography style={{ width: '100%' }}>Cancel</Typography>
        </ListItem>
      )}
      {cartId && (
        <ListItem button onClick={() => onClickShowCart(cartId)}>
          <Typography style={{ width: '100%' }}>Cart</Typography>
        </ListItem>
      )}
    </ActionsMenu>
  );
};

export const paymentsColumns = (paymentsProvider) => [
  gridColumn('customer', {
    flex: 1,
    sortableFields: paymentsProvider.sortable,
    minWidth: 150,
    renderCell: (params: GridRowParams) => {
      const { purchaseMethod } = params.row;
      const fullName = purchaseMethod?.fullName;
      const userId = purchaseMethod?.userId;

      return <TextLink to={userDetailsTabParameters(userId)} label={fullName} />;
    },
  }),
  gridColumn('method', {
    flex: 1,
    sortableFields: paymentsProvider.sortable,
    minWidth: 150,
    renderCell: (params: GridRowParams) => {
      const name = params.row.purchaseMethod?.name;
      return <div>{name}</div>;
    },
  }),
  gridColumn('reference', { flex: 2, sortableFields: paymentsProvider.sortable, minWidth: 150 }),
  gridColumn('price', {
    flex: 1,
    sortableFields: paymentsProvider.sortable,
    minWidth: 100,
    renderCell: (params: GridRowParams) => <div>{params.row.chargeAmount?.formatted}</div>,
  }),
  gridColumn('createdAt', {
    flex: 2,
    headerName: 'Date',
    sortableFields: paymentsProvider.sortable,
    minWidth: 150,
    renderCell: (params: GridRowParams) => {
      const date = new Date(params.row.createdAt);

      if (!date) {
        return <div />;
      }

      const sloDate = formatToSloDate(date);

      return <div>{sloDate}</div>;
    },
  }),
  gridColumn('location', {
    flex: 2,
    sortableFields: paymentsProvider.sortable,
    minWidth: 200,
    renderCell: (params: GridRowParams) => {
      const { location } = params.row;

      return location?.name ? (
        <TextLink
          divStyle={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}
          to={locationDetailsTabParameters(location.id, LOCATION_DETAILS_TABS.OVERVIEW)}
          label={location.name}
          title={location.name}
        />
      ) : (
        'N/A'
      );
    },
  }),
  gridColumn('status', {
    flex: 1,
    sortableFields: paymentsProvider.sortable,
    minWidth: 200,
    renderCell: (params) => <PaymentStatusBadge status={params.row.status} />,
  }),
  gridColumn(' ', {
    width: 60,
    align: 'left',
    headerAlign: 'left',
    renderCell: (params) => paymentActions(params, paymentsProvider),
  }),
];

export const PaymentsPage = observer(() => {
  const paymentsProvider = usePaginatedProvider((offset, sortBy, sortOrder) =>
    Api.billing.listPayments({ skip: offset, take: 20, sortBy, sortOrder }),
  );

  return (
    <div style={{ width: '100%' }}>
      <VMPaginatedGrid
        label="Payments"
        count={paymentsProvider.count}
        rows={paymentsProvider.data}
        columns={paymentsColumns(paymentsProvider)}
        onPageOffsetChange={paymentsProvider.onPageChange}
        onSortTypeChange={paymentsProvider.setSort}
      />
    </div>
  );
});
